import {
  INACTIVITY_CHANNEL_NAME,
  INACTIVITY_STORAGE_KEY,
  INACTIVITY_LOGOUT_KEY,
} from '../config-global';

/**
 * Inactivity Manager
 *
 * Handles cross-tab communication for user activity and logout events.
 * Uses BroadcastChannel API with localStorage fallback for older browsers.
 *
 * @class InactivityManager
 */
class InactivityManager {
  constructor() {
    this.channel = null;
    this.listeners = new Set();
    this.storageListener = null;
    this.initializeChannel();
  }

  /**
   * Initialize BroadcastChannel or fallback to localStorage
   * @private
   */
  initializeChannel() {
    // Try to use modern BroadcastChannel API
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(INACTIVITY_CHANNEL_NAME);
        this.channel.onmessage = (event) => {
          this.notifyListeners(event.data);
        };
      } catch (error) {
        console.warn('BroadcastChannel not available, using localStorage fallback');
        this.setupLocalStorageFallback();
      }
    } else {
      // Fallback to localStorage for older browsers
      this.setupLocalStorageFallback();
    }
  }

  /**
   * Setup localStorage-based cross-tab communication
   * @private
   */
  setupLocalStorageFallback() {
    this.storageListener = (e) => {
      // Listen for logout broadcasts
      if (e.key === INACTIVITY_LOGOUT_KEY && e.newValue) {
        this.notifyListeners({ type: 'logout', timestamp: Date.now() });
      }
      // Listen for activity updates
      if (e.key === INACTIVITY_STORAGE_KEY && e.newValue) {
        this.notifyListeners({ type: 'activity', timestamp: parseInt(e.newValue, 10) });
      }
    };
    window.addEventListener('storage', this.storageListener);
  }

  /**
   * Broadcast activity to all tabs
   * Updates last activity timestamp across all open tabs
   */
  broadcastActivity() {
    const timestamp = Date.now();

    try {
      if (this.channel) {
        this.channel.postMessage({ type: 'activity', timestamp });
      } else {
        // localStorage fallback
        localStorage.setItem(INACTIVITY_STORAGE_KEY, timestamp.toString());
      }
    } catch (error) {
      console.warn('Failed to broadcast activity:', error);
      // Continue - local timer still works even if broadcast fails
    }
  }

  /**
   * Broadcast logout to all tabs
   * Triggers logout across all open tabs of the application
   */
  broadcastLogout() {
    try {
      if (this.channel) {
        this.channel.postMessage({ type: 'logout', timestamp: Date.now() });
      } else {
        // localStorage fallback
        localStorage.setItem(INACTIVITY_LOGOUT_KEY, Date.now().toString());
        // Clean up immediately to allow future broadcasts
        setTimeout(() => {
          try {
            localStorage.removeItem(INACTIVITY_LOGOUT_KEY);
          } catch (e) {
            // Ignore cleanup errors
          }
        }, 100);
      }
    } catch (error) {
      console.warn('Failed to broadcast logout:', error);
      // Local logout still happens even if broadcast fails
    }
  }

  /**
   * Subscribe to cross-tab events
   * @param {Function} callback - Called when activity or logout is broadcast
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all subscribers of an event
   * @private
   */
  notifyListeners(data) {
    this.listeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error('Error in inactivity listener:', error);
      }
    });
  }

  /**
   * Cleanup resources
   * Should be called on app unmount (rare, but important for proper cleanup)
   */
  destroy() {
    if (this.channel) {
      this.channel.close();
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    this.listeners.clear();
  }
}

// Export singleton instance
export const inactivityManager = new InactivityManager();
